import { useState } from 'react'
import type { ChangeEvent, FormEvent, ReactNode } from 'react'
import { LoaderCircle, Mail, Send } from 'lucide-react'
import { DiscordIcon, SteamIcon } from '@/components/BrandIcons'
import { useLanguage } from '@/localization'
import type { CommunityData } from '@/useCommunity'

type SubmitStatus = 'idle' | 'success' | 'error'

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

const initialForm: FormData = {
  name: '',
  email: '',
  subject: '',
  message: '',
}

const subjects = [
  ['general', 'General Inquiry'],
  ['support', 'Technical Support'],
  ['community', 'Community / Discord'],
  ['minecraft', 'Minecraft'],
  ['kf1', 'Killing Floor'],
  ['suggestion', 'Suggestion'],
  ['other', 'Other'],
]

export default function ContactSection({ community }: { community: CommunityData }) {
  const [formData, setFormData] = useState<FormData>(initialForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')
  const { t } = useLanguage()
  const steamHref =
    community.social.find((social) => social.icon === 'steam')?.href ??
    'https://steamcommunity.com/groups/uliunailt'

  function handleInputChange(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (formData.message.length > 500) {
      setSubmitStatus('error')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, site: 'hub' }),
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      setSubmitStatus('success')
      setFormData(initialForm)
    } catch {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="scroll-mt-20 px-6 py-20">
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-stone-line bg-white px-4 py-1.5 font-rajdhani text-xs font-semibold uppercase tracking-[0.25em] text-uliunai-red shadow-card">
            <Mail size={18} />
            {t('Contact')}
          </span>
          <h2 className="metal-text mt-5 font-orbitron text-4xl font-black tracking-tight sm:text-5xl">
            {t('Get In Touch')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl font-rajdhani text-lg text-stone-600">
            {t(
              'Questions, reports, suggestions, or community help. Send a message and it lands with the Uliunai team.',
            )}
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
          <form
            id="contact-form"
            onSubmit={handleSubmit}
            className="rounded-2xl border border-stone-line bg-white p-6 shadow-card sm:p-8"
          >
            <h3 className="mb-6 font-orbitron text-2xl font-bold text-stone-900">
              {t('Send us a Message')}
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name *" id="hub-contact-name">
                <input
                  id="hub-contact-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('Your name')}
                  className={inputClass}
                />
              </Field>
              <Field label="Email *" id="hub-contact-email">
                <input
                  id="hub-contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder={t('your@email.com')}
                  className={inputClass}
                />
              </Field>
            </div>

            <Field label="Subject *" id="hub-contact-subject" className="mt-4">
              <select
                id="hub-contact-subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className={inputClass}
              >
                <option value="">{t('Select a subject')}</option>
                {subjects.map(([value, label]) => (
                  <option key={value} value={value}>
                    {t(label)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Message * (Max 500 characters)" id="hub-contact-message" className="mt-4">
              <textarea
                id="hub-contact-message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                maxLength={500}
                rows={6}
                placeholder={t('Your message here...')}
                className={`${inputClass} resize-none`}
              />
              <div className="mt-1 text-right font-rajdhani text-xs text-stone-500">
                {formData.message.length}/500 {t('characters')}
              </div>
            </Field>

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-uliunai-red px-6 py-3 font-orbitron text-sm font-bold uppercase tracking-wider text-white shadow-glow transition-colors hover:bg-uliunai-red-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <LoaderCircle size={16} className="animate-spin" />
                  {t('Sending...')}
                </>
              ) : (
                <>
                  <Send size={16} />
                  {t('Send Message')}
                </>
              )}
            </button>

            {submitStatus === 'success' && (
              <p className="mt-4 text-center font-rajdhani font-semibold text-green-700">
                {t('Message sent successfully.')}
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="mt-4 text-center font-rajdhani font-semibold text-uliunai-red">
                {t('Failed to send message. Please try again or use Discord.')}
              </p>
            )}
          </form>

          <div className="space-y-5">
            <InfoCard
              icon={<DiscordIcon size={22} />}
              title="Discord Server"
              body="Fastest way to reach the community."
              href={community.discordInvite}
              label="Open Discord"
            />
            <InfoCard
              icon={<SteamIcon size={22} />}
              title="Steam Group"
              body="Follow Uliunai updates and events."
              href={steamHref}
              label="Steam community"
            />
            <div className="rounded-2xl border border-stone-line bg-white p-6 shadow-card">
              <h3 className="font-orbitron text-xl font-bold text-stone-900">Quick Support</h3>
              <p className="mt-3 font-rajdhani text-stone-600">
                {t(
                  'For urgent server issues, Discord is usually fastest. Contact form messages are still routed to staff.',
                )}
              </p>
              <div className="mt-4 space-y-1 font-rajdhani text-sm text-stone-500">
                <p>
                  <strong className="text-stone-800">{t('Response Time:')}</strong>{' '}
                  {t('Usually within 1-2 hours')}
                </p>
                <p>
                  <strong className="text-stone-800">{t('Support Hours:')}</strong>{' '}
                  {t('24/7 via Discord')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const inputClass =
  'w-full rounded-xl border border-stone-line bg-parchment px-4 py-3 font-rajdhani text-sm text-stone-900 placeholder:text-stone-400 transition-colors focus:border-uliunai-red focus:bg-white focus:outline-none focus:ring-2 focus:ring-uliunai-red/20'

function Field({
  label,
  id,
  className = '',
  children,
}: {
  label: string
  id: string
  className?: string
  children: ReactNode
}) {
  const { t } = useLanguage()

  return (
    <div className={className}>
      <label htmlFor={id} className="mb-2 block font-rajdhani text-sm font-semibold text-stone-700">
        {t(label)}
      </label>
      {children}
    </div>
  )
}

function InfoCard({
  icon,
  title,
  body,
  href,
  label,
}: {
  icon: ReactNode
  title: string
  body: string
  href: string
  label: string
}) {
  const { t } = useLanguage()

  return (
    <div className="rounded-2xl border border-uliunai-red/20 bg-gradient-to-br from-white to-red-50/70 p-6 shadow-card">
      <div className="flex gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-uliunai-red text-white shadow-glow">
          {icon}
        </span>
        <div className="min-w-0">
          <h3 className="font-orbitron text-lg font-bold text-stone-900">{t(title)}</h3>
          <p className="font-rajdhani text-stone-600">{t(body)}</p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex font-rajdhani font-bold text-uliunai-red transition-colors hover:text-uliunai-red-dark"
          >
            {t(label)}
          </a>
        </div>
      </div>
    </div>
  )
}
